import { z } from 'zod'
import { browserAssetUrlSchema } from '../helpers/asset-ref.js'
import { focalPointSchema } from '../helpers/focal-point.js'
import { vendorServiceSchema } from '../enums/vendor-service.enum.js'
import { vendorOfferingKindSchema } from '../enums/vendor-offering-kind.enum.js'
import { vendorPriceUnitSchema } from '../enums/vendor-price-unit.enum.js'
import { vendorPriceVisibilitySchema } from '../enums/vendor-price-visibility.enum.js'
import { vendorMediaKindSchema } from '../enums/vendor-media-kind.enum.js'
import { offeringDetailsReadSchemas, type OfferedService } from '../input/vendor-offering.input.js'
import { noReviews, shownVendorReviewsDtoSchema } from './vendor-review.dto.js'

/**
 * A vendor's profile as others see it (docs/vendor-growth.spec.md): venue
 * staff and planners, in this slice. Images are signed for an hour, like every
 * other private image.
 */

const HTTP_LINK = /^https?:\/\//i

/**
 * A link a profile puts in `href`, read leniently: anything but an http(s)
 * address arrives as null instead of refusing the whole profile, which the
 * public page renders from. Saving is held to the strict rules
 * (`websiteSchema`, `socialUrlSchema`); this only keeps one bad row from either
 * running in a visitor's browser or taking the page down.
 */
const shownLink = z
  .string()
  .nullable()
  .transform((link) => (link !== null && HTTP_LINK.test(link) ? link : null))

/** Contact details the company chose to show. */
export const vendorContactDtoSchema = z.object({
  website: shownLink,
  phone: z.string().nullable(),
  publicEmail: z.string().nullable(),
  instagramUrl: shownLink,
  facebookUrl: shownLink,
  tiktokUrl: shownLink,
  pinterestUrl: shownLink,
})

export type VendorContactDto = z.infer<typeof vendorContactDtoSchema>

export const vendorPhotoDtoSchema = z.object({
  id: z.string().uuid(),
  url: browserAssetUrlSchema,
  caption: z.string().nullable(),
  focalPoint: focalPointSchema.nullable(),
  sortOrder: z.number().int(),
})

export const vendorMediaLinkDtoSchema = z.object({
  id: z.string().uuid(),
  kind: vendorMediaKindSchema,
  url: z.string(),
  title: z.string().nullable(),
})

/** A profile's media links, without one whose address is not http(s): it is linked to, and it is dropped rather than failing the profile. */
export const vendorMediaLinkListDtoSchema = z
  .array(vendorMediaLinkDtoSchema)
  .transform((links) => links.filter((link) => HTTP_LINK.test(link.url)))

/** A price as others read it. Absent (null) when it is on request: there is no number to send. */
export const offeringPriceDtoSchema = z.object({
  cents: z.number().int(),
  from: z.boolean(),
  unit: vendorPriceUnitSchema,
})

export type OfferingPriceDto = z.infer<typeof offeringPriceDtoSchema>

export const offeringPhotoDtoSchema = z.object({
  url: browserAssetUrlSchema,
  caption: z.string().nullable(),
})

const offeringDtoBase = {
  id: z.string().uuid(),
  kind: vendorOfferingKindSchema,
  name: z.string(),
  description: z.string(),
  price: offeringPriceDtoSchema.nullable(),
  photos: z.array(offeringPhotoDtoSchema),
  position: z.number().int(),
}

/** What the company itself typed about the price, for its own editor. */
const ownPriceFields = {
  priceVisibility: vendorPriceVisibilitySchema,
  priceCents: z.number().int().nullable(),
  priceFrom: z.boolean(),
  priceUnit: vendorPriceUnitSchema,
}

function offering<S extends OfferedService, D extends z.ZodType>(service: S, details: D) {
  return z.object({ service: z.literal(service), details, ...offeringDtoBase })
}

function ownOffering<S extends OfferedService, D extends z.ZodType>(service: S, details: D) {
  return z.object({ service: z.literal(service), details, ...offeringDtoBase, ...ownPriceFields })
}

/** A package or item, its fields typed by its service. */
export const vendorOfferingDtoSchema = z.discriminatedUnion('service', [
  offering('catering', offeringDetailsReadSchemas.catering),
  offering('photography', offeringDetailsReadSchemas.photography),
  offering('entertainment', offeringDetailsReadSchemas.entertainment),
  offering('floral', offeringDetailsReadSchemas.floral),
  offering('beauty', offeringDetailsReadSchemas.beauty),
  offering('transportation', offeringDetailsReadSchemas.transportation),
  offering('lighting', offeringDetailsReadSchemas.lighting),
  offering('av', offeringDetailsReadSchemas.av),
  offering('planning', offeringDetailsReadSchemas.planning),
])

export type VendorOfferingDto = z.infer<typeof vendorOfferingDtoSchema>

/** The company's own package or item: as others see it, plus what it typed about the price. */
export const ownVendorOfferingDtoSchema = z.discriminatedUnion('service', [
  ownOffering('catering', offeringDetailsReadSchemas.catering),
  ownOffering('photography', offeringDetailsReadSchemas.photography),
  ownOffering('entertainment', offeringDetailsReadSchemas.entertainment),
  ownOffering('floral', offeringDetailsReadSchemas.floral),
  ownOffering('beauty', offeringDetailsReadSchemas.beauty),
  ownOffering('transportation', offeringDetailsReadSchemas.transportation),
  ownOffering('lighting', offeringDetailsReadSchemas.lighting),
  ownOffering('av', offeringDetailsReadSchemas.av),
  ownOffering('planning', offeringDetailsReadSchemas.planning),
])

export type OwnVendorOfferingDto = z.infer<typeof ownVendorOfferingDtoSchema>

/**
 * A venue or a planning company vouching for a company, by name
 * (docs/vendor-growth.spec.md, "Reputation"). Names only: whose ids they are is
 * nobody else's business, and other organizations' venues least of all.
 */
export const vendorRecommendationDtoSchema = z.object({
  by: z.enum(['venue', 'planner']),
  name: z.string(),
})

export type VendorRecommendationDto = z.infer<typeof vendorRecommendationDtoSchema>

const profileBase = {
  vendorId: z.string().uuid(),
  name: z.string(),
  services: z.array(vendorServiceSchema),
  logoUrl: browserAssetUrlSchema.nullable(),
  coverImageUrl: browserAssetUrlSchema.nullable(),
  description: z.string().nullable(),
  /** Where it is and how far it travels: city and state, never the street. */
  area: z.object({
    city: z.string().nullable(),
    state: z.string().nullable(),
    serviceRadiusMiles: z.number().int().nullable(),
  }),
  contact: vendorContactDtoSchema,
  photos: z.array(vendorPhotoDtoSchema),
  mediaLinks: vendorMediaLinkListDtoSchema,
  /** Who vouches for it: venues first, then planning companies, each by name. Defaulted while an older API is still answering. */
  recommendations: z.array(vendorRecommendationDtoSchema).default([]),
}

/** A company's profile, as venue staff and planners see it. */
export const vendorProfileDtoSchema = z.object({
  ...profileBase,
  /** The reader's own planning company recommends it; always false for anyone else. */
  recommendedByYou: z.boolean().default(false),
  /** Only for services it still offers. */
  offerings: z.array(vendorOfferingDtoSchema),
  /** Its published reviews. Defaulted while an older API is still answering. */
  reviews: shownVendorReviewsDtoSchema.default(noReviews),
})

export type VendorProfileDto = z.infer<typeof vendorProfileDtoSchema>

/** The company's own profile, for its editor: every offering, with the price as typed. */
export const ownVendorProfileDtoSchema = z.object({
  ...profileBase,
  /** Including any for a service it has since stopped offering, which nobody else sees. */
  offerings: z.array(ownVendorOfferingDtoSchema),
})

export type OwnVendorProfileDto = z.infer<typeof ownVendorProfileDtoSchema>

/** The steps of a company's profile checklist, in the order it shows them; each is a card on the Profile page. */
export const PROFILE_CHECKLIST_STEPS = ['logo', 'description', 'contact', 'photos', 'offerings'] as const

export type ProfileChecklistStepId = (typeof PROFILE_CHECKLIST_STEPS)[number]

/**
 * What a company still has to add to its profile, and why it is worth adding
 * (docs/vendor-growth.spec.md, "First sign-in"): shown to everyone at the
 * company until every step is done.
 */
export const profileChecklistDtoSchema = z.object({
  steps: z.array(
    z.object({
      id: z.enum(PROFILE_CHECKLIST_STEPS),
      label: z.string(),
      why: z.string(),
      done: z.boolean(),
      /**
       * Where it is done: the Profile page, at the step's own card; the
       * furniture catalog, for pieces; or Settings, to choose what the
       * company offers.
       */
      place: z.enum(['profile', 'furniture', 'settings']),
    }),
  ),
  done: z.boolean(),
})

export type ProfileChecklistDto = z.infer<typeof profileChecklistDtoSchema>
